import { Fragment, useEffect, useState } from "react";
import { Dialog, Popover, Tab, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  ShoppingBagIcon,
  XMarkIcon,
  ChevronDownIcon
} from "@heroicons/react/24/outline";

import { Link, useLocation, useNavigate } from "react-router-dom";
import { Avatar, Button, Menu, MenuItem } from "@mui/material";
import { navigation } from "../../../config/navigationMenu";
import { useDispatch, useSelector } from "react-redux";
import { deepPurple } from "@mui/material/colors";
import { getUser, logout } from "../../../Redux/Auth/Action";
import { getCart } from "../../../Redux/Customers/Cart/Action";
import TextField from "@mui/material/TextField";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Navigation({ onOpenAuthModal }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { auth, cart } = useSelector((store) => store);
  const jwt = localStorage.getItem("jwt");
  const [anchorEl, setAnchorEl] = useState(null);
  const openUserMenu = Boolean(anchorEl);
  const location = useLocation();

  useEffect(() => {
    if (jwt) {
      dispatch(getUser(jwt));
      dispatch(getCart(jwt));
    }
  }, [jwt, dispatch]);

  const handleUserClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseUserMenu = (event) => {
    setAnchorEl(null);
  };

  const handleOpen = () => {
    // Navigate to login route - CustomerRoutes useEffect will handle opening the modal
    navigate("/login");
    if (onOpenAuthModal) {
      onOpenAuthModal();
    }
  };

  const handleCategoryClick = (category, section, item, close) => {
    navigate(`/${category.id}/${section.id}/${item.id}`);
    close();
  };

  // Akhil comments - Removed navigate(-1) logic as it interfered with AuthModal redirect
  // The AuthModal now handles path changes properly

  const handleLogout = () => {
    handleCloseUserMenu();
    dispatch(logout());
    navigate("/");
  };
  const handleMyOrderClick = () => {
    handleCloseUserMenu();
    auth.user?.role === "ROLE_ADMIN"
      ? navigate("/admin")
      : navigate("/account/order");
  };

  const handleChangePasswordClick = () => {
    handleCloseUserMenu();
    navigate("/account/change-password");
  };

  return (
    <div className="bg-white">
      {/* Mobile menu */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-col overflow-y-auto bg-white pb-12 shadow-xl pt-16">
                <div className="flex px-4 pb-2 pt-2">
                  <button
                    type="button"
                    className="-m-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Static Navigation Links */}
                <div className="px-4 py-4 border-b border-gray-200">
                  <p
                    className="block py-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-50 rounded-md px-2"
                    onClick={() => {
                      navigate("/");
                      setOpen(false);
                    }}
                  >
                    Home
                  </p>
                  <p
                    className="block py-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-50 rounded-md px-2"
                    onClick={() => {
                      navigate("/");
                      setOpen(false);
                    }}
                  >
                    Best Sellers
                  </p>
                </div>

                {/* Shop Categories */}
                <div className="px-4 py-4 border-b border-gray-200">
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 px-2">
                    Shop
                  </p>
                  {navigation.categories.map((category) => {
                    const isSeasonalFruits = category.id === 'seasonal_fruits';
                    const isTropicalFruits = category.id === 'tropical_fruits';
                    return (
                      <div key={category.name} className="mb-4">
                        <p className="font-medium text-gray-900 mb-2 px-2">
                          {category.name}
                        </p>
                        <div className="pl-4">
                          {category.sections.map((section) => {
                            const isSeasonalProduce = section.id === 'seasonal_produce';
                            const isFreshFruits = section.id === 'fresh_fruits';
                            const isClickable = (isSeasonalFruits && isSeasonalProduce) || (isTropicalFruits && isFreshFruits);
                            return (
                              <p
                                key={section.name}
                                className={isClickable
                                  ? "block py-2 px-2 text-gray-600 cursor-pointer hover:text-organic-green hover:bg-gray-50 rounded-md"
                                  : "block py-2 px-2 text-gray-400 cursor-not-allowed rounded-md"
                                }
                                onClick={isClickable ? () => {
                                  navigate(`/${category.id}/${section.id}`);
                                  setOpen(false);
                                } : undefined}
                              >
                                {section.name}
                              </p>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* More Static Links */}
                <div className="px-4 py-4 border-b border-gray-200">
                  <p
                    className="block py-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-50 rounded-md px-2"
                    onClick={() => {
                      navigate("/");
                      setOpen(false);
                    }}
                  >
                    FAQ
                  </p>
                  <p
                    className="block py-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-50 rounded-md px-2"
                    onClick={() => {
                      navigate("/about");
                      setOpen(false);
                    }}
                  >
                    About Us
                  </p>
                </div>

                {/* Sign In / Account */}
                <div className="px-4 py-4">
                  {auth.user ? (
                    <>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md mb-2">
                        <Avatar
                          sx={{ width: 32, height: 32, bgcolor: "#4A7C3F", color: "white" }}
                        >
                          {auth.user?.firstName?.[0]?.toUpperCase() || 'U'}
                        </Avatar>
                        <span className="font-medium text-gray-900">
                          {auth.user?.firstName} {auth.user?.lastName}
                        </span>
                      </div>
                      <p
                        className="block py-3 px-2 text-gray-600 cursor-pointer hover:bg-gray-50 rounded-md"
                        onClick={() => {
                          navigate("/account/order");
                          setOpen(false);
                        }}
                      >
                        My Orders
                      </p>
                      {auth.user?.role === "ROLE_ADMIN" && (
                        <p
                          className="block py-3 px-2 text-gray-600 cursor-pointer hover:bg-gray-50 rounded-md"
                          onClick={() => {
                            navigate("/admin");
                            setOpen(false);
                          }}
                        >
                          Admin Dashboard
                        </p>
                      )}
                      <p
                        className="block py-3 px-2 text-gray-600 cursor-pointer hover:bg-gray-50 rounded-md"
                        onClick={() => {
                          navigate("/account/change-password");
                          setOpen(false);
                        }}
                      >
                        Change Password
                      </p>
                      <p
                        className="block py-3 px-2 text-red-600 cursor-pointer hover:bg-red-50 rounded-md"
                        onClick={() => {
                          dispatch(logout());
                          navigate("/");
                          setOpen(false);
                        }}
                      >
                        Logout
                      </p>
                    </>
                  ) : (
                    <p
                      className="block py-3 px-2 font-medium text-organic-green cursor-pointer hover:bg-gray-50 rounded-md"
                      onClick={() => {
                        navigate("/login");
                        if (onOpenAuthModal) onOpenAuthModal();
                        setOpen(false);
                      }}
                    >
                      Sign In
                    </p>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <header className="sticky top-0 z-50 bg-organic-green shadow-lg">
        {/* Akhil comments - Promotional banner hidden as per requirement
        <p className="flex h-10 items-center justify-center bg-organic-charcoal px-4 text-sm font-medium text-white sm:px-6 lg:px-8">
          Get free delivery on orders over $100
        </p>
        */}
        <nav aria-label="Top" className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="">
            {/* Top Row: Logo Left, Links Center, Icons Right */}
            <div className="flex h-28 items-center justify-between relative">

              {/* Mobile Menu Button (Left) */}
              <button
                type="button"
                className="rounded-md bg-organic-green p-2 text-white lg:hidden absolute left-0"
                onClick={() => setOpen(true)}
              >
                <span className="sr-only">Open menu</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>

              {/* Left Key Logo (Desktop) - Reinstated */}
              <div className="hidden lg:flex absolute left-0 top-0 items-center h-full ml-4 z-10">
                <Link to="/" className="h-full flex items-center">
                  <img src="/images/siri_farms.png" alt="Siri Farms" className="h-[90%] w-auto object-contain" />
                </Link>
              </div>

              {/* Centered Navigation Links - Moved from Bottom Row */}
              <div className="hidden lg:flex flex-1 justify-center">
                <Popover.Group className="flex items-center space-x-8">
                  {/* Home Link */}
                  <Link to="/" className="flex items-center text-sm font-bold uppercase text-white hover:text-organic-beige tracking-wider">
                    Home
                  </Link>

                  {/* Best Sellers */}
                  <Link to="/" className="flex items-center text-sm font-bold uppercase text-white hover:text-organic-beige tracking-wider">
                    Best Sellers
                  </Link>

                  {/* Shop Dropdown */}
                  <Popover className="relative">
                    {({ open, close }) => (
                      <>
                        <Popover.Button
                          className={classNames(
                            open ? "text-organic-beige" : "text-white hover:text-organic-beige",
                            "group inline-flex items-center text-sm font-bold uppercase tracking-wider focus:outline-none"
                          )}
                        >
                          <span>Shop</span>
                          <ChevronDownIcon
                            className={classNames(
                              open ? "text-organic-beige" : "text-white group-hover:text-organic-beige",
                              "ml-2 h-4 w-4"
                            )}
                            aria-hidden="true"
                          />
                        </Popover.Button>

                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-200"
                          enterFrom="opacity-0 translate-y-1"
                          enterTo="opacity-100 translate-y-0"
                          leave="transition ease-in duration-150"
                          leaveFrom="opacity-100 translate-y-0"
                          leaveTo="opacity-0 translate-y-1"
                        >
                          <Popover.Panel className="absolute left-1/2 z-50 mt-5 w-64 -translate-x-1/2 px-2 sm:px-0">
                            <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                              <div className="relative flex flex-col gap-6 bg-white px-6 py-6 sm:p-6">
                                {navigation.categories.map((category) => {
                                  const isSeasonalFruits = category.id === 'seasonal_fruits';
                                  const isTropicalFruits = category.id === 'tropical_fruits';
                                  return (
                                    <div key={category.name} className="flex flex-col gap-3">
                                      <p className="text-base font-bold text-gray-900 border-b pb-2 mb-1">
                                        {category.name}
                                      </p>
                                      <div className="flex flex-col gap-2">
                                        {category.sections.map((section) => {
                                          const isSeasonalProduce = section.id === 'seasonal_produce';
                                          const isFreshFruits = section.id === 'fresh_fruits';
                                          const isClickable = (isSeasonalFruits && isSeasonalProduce) || (isTropicalFruits && isFreshFruits);
                                          return (
                                            <p
                                              key={section.name}
                                              onClick={isClickable ? () => {
                                                navigate(`/${category.id}/${section.id}`);
                                                close();
                                              } : undefined}
                                              className={isClickable
                                                ? "cursor-pointer text-sm font-medium text-gray-600 hover:text-organic-green hover:pl-1 transition-all"
                                                : "cursor-not-allowed text-sm font-medium text-gray-400"
                                              }
                                            >
                                              {section.name}
                                            </p>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </Popover.Panel>
                        </Transition>
                      </>
                    )}
                  </Popover>

                  {/* Other Static Links */}
                  <Link to="/" className="flex items-center text-sm font-bold uppercase text-white hover:text-organic-beige tracking-wider">
                    FAQ
                  </Link>
                  <Link to="/about" className="flex items-center text-sm font-bold uppercase text-white hover:text-organic-beige tracking-wider">
                    About Us
                  </Link>
                </Popover.Group>
              </div>

              {/* Right Side Icons */}
              <div className="absolute right-0 flex items-center">
                <div className="flex items-center lg:space-x-6">
                  {/* Search */}
                  <div className="flex items-center">
                    <p onClick={() => navigate("/products/search")} className="p-2 text-white hover:text-organic-beige cursor-pointer">
                      <span className="sr-only">Search</span>
                      <MagnifyingGlassIcon className="h-6 w-6" aria-hidden="true" />
                    </p>
                  </div>

                  {/* Account */}
                  <div className="hidden lg:flex">
                    {auth.user ? (
                      <div className="relative group">
                        <Avatar
                          className="text-white bg-organic-beige text-organic-charcoal cursor-pointer"
                          onClick={handleUserClick}
                          aria-controls={openUserMenu ? "basic-menu" : undefined}
                          aria-haspopup="true"
                          aria-expanded={openUserMenu ? "true" : undefined}
                          sx={{ width: 32, height: 32, bgcolor: "#F5F5DC", color: "#1A1A1A" }}
                        >
                          {auth.user?.firstName?.[0]?.toUpperCase() || 'U'}
                        </Avatar>
                        <Menu
                          id="basic-menu"
                          anchorEl={anchorEl}
                          open={openUserMenu}
                          onClose={handleCloseUserMenu}
                          MenuListProps={{
                            "aria-labelledby": "basic-button",
                          }}
                        >
                          {/* Akhil comments - Admin Dashboard only visible to ROLE_ADMIN users */}
                          {auth.user?.role === "ROLE_ADMIN" && (
                            <MenuItem onClick={() => { handleCloseUserMenu(); navigate("/admin"); }}>
                              Admin Dashboard
                            </MenuItem>
                          )}
                          {/* My Orders - enabled for all users */}
                          <MenuItem onClick={() => { handleCloseUserMenu(); navigate("/account/order"); }}>
                            My Orders
                          </MenuItem>
                          <MenuItem onClick={handleChangePasswordClick}>Change Password</MenuItem>
                          <MenuItem onClick={handleLogout}>Logout</MenuItem>
                        </Menu>
                      </div>
                    ) : (
                      <Button
                        onClick={handleOpen}
                        className="text-sm font-bold hover:text-white"
                        sx={{
                          color: 'black',
                          minWidth: 'auto',
                          fontWeight: 'bold',
                          '&:hover': { color: 'white' }
                        }}
                      >
                        Signin
                      </Button>
                    )}
                  </div>

                  {/* Cart */}
                  <div className="flow-root">
                    <Button
                      onClick={() => navigate("/cart")}
                      className="group -m-2 flex items-center p-2"
                      sx={{ color: 'white', minWidth: 'auto' }}
                    >
                      <ShoppingBagIcon
                        className="h-6 w-6 flex-shrink-0 text-white group-hover:text-organic-beige"
                        aria-hidden="true"
                      />
                      <span className="ml-2 text-sm font-medium text-white group-hover:text-organic-beige">
                        {cart.cart?.totalItem}
                      </span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </nav>
      </header>
    </div>
  );
}
